
import { useState, useEffect, useRef, useCallback } from 'react';

interface UsePlaybackProps {
  totalDuration: number;
}

export const usePlayback = ({ totalDuration }: UsePlaybackProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const intervalRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);

  const clearExistingInterval = () => {
    if (intervalRef.current) {
      cancelAnimationFrame(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const play = useCallback(() => {
    if (totalDuration > 0) {
      setIsPlaying(true);
      lastFrameTimeRef.current = performance.now();
    }
  }, [totalDuration]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    clearExistingInterval();
  }, []);

  const seek = useCallback((time: number) => {
    const newTime = Math.max(0, Math.min(time, totalDuration));
    setCurrentTime(newTime);
  }, [totalDuration]);

  const setSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      const animate = (timestamp: number) => {
        const deltaTime = timestamp - lastFrameTimeRef.current;
        lastFrameTimeRef.current = timestamp;

        setCurrentTime(prevTime => {
          const newTime = prevTime + deltaTime * playbackSpeed;
          if (newTime >= totalDuration) {
            pause();
            return totalDuration;
          }
          return newTime;
        });
        
        intervalRef.current = requestAnimationFrame(animate);
      };
      intervalRef.current = requestAnimationFrame(animate);
    } else {
      clearExistingInterval();
    }

    return () => clearExistingInterval();
  }, [isPlaying, playbackSpeed, totalDuration, pause]);
  
  // Reset time if duration changes
  useEffect(() => {
      setCurrentTime(0);
      pause();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDuration])

  return { currentTime, isPlaying, playbackSpeed, play, pause, seek, setSpeed };
};
